import { z } from "zod";
import {
  httpUrl,
  optionalEnum,
  optionalExternalId,
  optionalUuid,
} from "./_helpers/optional";

export const communicationChannels = [
  "sms",
  "email",
  "call",
  "chat",
  "note",
  "voicemail",
] as const;
export type CommunicationChannel = (typeof communicationChannels)[number];

export const communicationDirections = [
  "inbound",
  "outbound",
  "internal",
] as const;
export type CommunicationDirection = (typeof communicationDirections)[number];

// Mirrors the polymorphic targets supported by core.communications.entity_type.
// Kept in sync with ENTITY_OPPORTUNITY_LOOKUP in services/comms/communications.ts.
// ADV-1180 added warranty (obligation) and warranty_claim so notes /
// EntityActivityFeed can target warranty rows. warranty_term is a template
// and not a comms target. ADV-1181 added service_agreement (per-customer
// instance) on the same precedent — service_plan is a template and excluded.
// ADV-782 added territory (perennial entity, no parent opportunity) so the
// territories/:id detail page can mount EntityActivityFeed/Preview.
// ADV-783 added team_member on the same precedent — the v2 Team Members
// detail page mounts <EntityActivityFeed entityType="team_member" …>;
// team_member has no parent opportunity (mirrors customer in
// ENTITY_OPPORTUNITY_LOOKUP).
// core.communications.entity_type has no DB CHECK constraint — the union here
// is the canonical enforcement layer.
export const communicationEntityTypes = [
  "opportunity",
  "job",
  "appointment",
  "proposal",
  "invoice",
  "customer",
  "warranty",
  "warranty_claim",
  "service_agreement",
  "territory",
  "team_member",
] as const;
export type CommunicationEntityType = (typeof communicationEntityTypes)[number];

// ADV-714: where in the work lifecycle this activity occurred.
// Kept in sync with the CHECK constraint in
// supabase/migrations/20260503204823_adv_714_communication_attachments.sql.
export const communicationLifecycleStages = [
  "pre_appointment",
  "during_appointment",
  "post_appointment",
  "warranty_visit",
  "office",
] as const;
export type CommunicationLifecycleStage =
  (typeof communicationLifecycleStages)[number];

// ADV-714: how this activity was created. Lets the timeline distinguish between
// crew-app captures, office uploads, customer-supplied (SMS/MMS) photos,
// system-generated events, bulk imports, and demo-generator output.
export const communicationSources = [
  "crew_app",
  "office_upload",
  "customer_provided",
  "system",
  "import",
  "generated_demo",
] as const;
export type CommunicationSource = (typeof communicationSources)[number];

export const CommunicationSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  opportunity_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Opportunity"),
  contact_id: z.string().uuid().nullable().optional().describe("Contact"),
  contact_role_id: z
    .string()
    .uuid()
    .nullable()
    .optional()
    .describe("Contact Role"),
  actor_role_id: z.string().uuid().nullable().optional().describe("Author"),
  entity_type: z
    .enum(communicationEntityTypes)
    .nullable()
    .optional()
    .describe("Entity Type"),
  entity_id: z.string().uuid().nullable().optional().describe("Entity"),
  lifecycle_stage: z
    .enum(communicationLifecycleStages)
    .nullable()
    .optional()
    .describe("Lifecycle Stage"),
  source: z.enum(communicationSources).nullable().optional().describe("Source"),
  attachment_count: z.number().int().nonnegative().describe("Attachment Count"),
  channel: z.enum(communicationChannels).describe("Channel"),
  direction: z.enum(communicationDirections).describe("Direction"),
  thread_id: z.string().uuid().nullable().optional().describe("Thread"),
  reply_to_id: z.string().uuid().nullable().optional().describe("Reply To"),
  subject: z.string().nullable().optional().describe("Subject"),
  body: z.string().describe("Body"),
  body_html: z.string().nullable().optional().describe("Body HTML"),
  from_address: z.string().nullable().optional().describe("From"),
  to_addresses: z.array(z.string()).nullable().optional().describe("To"),
  cc_addresses: z.array(z.string()).nullable().optional().describe("CC"),
  bcc_addresses: z.array(z.string()).nullable().optional().describe("BCC"),
  attachments: z
    .array(z.unknown())
    .nullable()
    .optional()
    .describe("Attachments"),
  external_id: z.string().nullable().optional().describe("External ID"),
  external_thread_id: z
    .string()
    .nullable()
    .optional()
    .describe("External Thread ID"),
  provider: z.string().nullable().optional().describe("Provider"),
  sent_at: z.string().describe("Sent At"),
  delivered_at: z.string().nullable().optional().describe("Delivered At"),
  read_at: z.string().nullable().optional().describe("Read At"),
  failed_at: z.string().nullable().optional().describe("Failed At"),
  failure_reason: z.string().nullable().optional().describe("Failure Reason"),
  metadata: z.record(z.unknown()).nullable().optional().describe("Metadata"),
  created_at: z.string().describe("Created At"),
  // ADV-1344: resolved author identity for the activity feed. Populated only
  // when the row is read through core.communications_feed_v (the entity /
  // opportunity list paths) — the LATERAL resolves actor_role_id →
  // contact_roles → contacts (name) + team_member_attributes.headshot_url
  // (avatar), business-scoped. Optional + nullable so non-feed reads of the
  // base table (which never project these) and system-authored rows degrade
  // to a monogram with no link.
  actor_name: z.string().nullable().optional().describe("Author Name"),
  actor_avatar_url: z.string().nullable().optional().describe("Author Avatar"),
});
export type Communication = z.infer<typeof CommunicationSchema>;

// Insert payload — server-generated fields stripped. business_id and account_id
// come from TenantContext; created_at defaults to now() in the database.
export const CommunicationInsertSchema = z.object({
  opportunity_id: optionalUuid().describe("Opportunity"),
  contact_id: optionalUuid().describe("Contact"),
  contact_role_id: optionalUuid().describe("Contact Role"),
  actor_role_id: optionalUuid().describe("Author"),
  entity_type: optionalEnum(communicationEntityTypes).describe("Entity Type"),
  entity_id: optionalUuid().describe("Entity"),
  lifecycle_stage: optionalEnum(communicationLifecycleStages).describe(
    "Lifecycle Stage",
  ),
  source: optionalEnum(communicationSources).describe("Source"),
  channel: z.enum(communicationChannels).describe("Channel"),
  direction: z.enum(communicationDirections).describe("Direction"),
  thread_id: optionalUuid().describe("Thread"),
  reply_to_id: optionalUuid().describe("Reply To"),
  subject: z.string().optional().describe("Subject"),
  body: z.string().describe("Body"),
  body_html: z.string().optional().describe("Body HTML"),
  from_address: z.string().optional().describe("From"),
  to_addresses: z.array(z.string()).optional().describe("To"),
  cc_addresses: z.array(z.string()).optional().describe("CC"),
  bcc_addresses: z.array(z.string()).optional().describe("BCC"),
  attachments: z.array(z.unknown()).optional().describe("Attachments"),
  external_id: z.string().optional().describe("External ID"),
  external_thread_id: z.string().optional().describe("External Thread ID"),
  provider: z.string().optional().describe("Provider"),
  sent_at: z.string().describe("Sent At"),
  delivered_at: z.string().optional().describe("Delivered At"),
  read_at: z.string().optional().describe("Read At"),
  failed_at: z.string().optional().describe("Failed At"),
  failure_reason: z.string().optional().describe("Failure Reason"),
  metadata: z.record(z.unknown()).optional().describe("Metadata"),
});
export type CommunicationInsert = z.input<typeof CommunicationInsertSchema>;

// ADV-1269: typed element for an imported communication attachment. The base
// CommunicationInsertSchema.attachments is z.array(z.unknown()), which let a
// tenant-supplied `javascript:`/`data:` URL pass schema validation on the
// import path and get stored verbatim into core.communication_attachments.url
// (the render-layer safeHttpUrl() guard from ADV-1254 then neutralized it at
// the sink, so this was defense-in-depth, not a live XSS). Restrict the url to
// absolute http(s) via the shared httpUrl() helper (ADR-0044) so a non-http(s)
// attachment URL never reaches the DB. The fields here mirror exactly what
// createCommunicationWithMediaPipeline() reads off each attachment
// (import-entity-handlers.ts) — url (required), ordinal, caption, metadata —
// and .passthrough() preserves any other fields on the row so we don't drop
// data. A malformed url fails the row cleanly (consistent with the resilient
// row-level validation in import-service), not silently.
export const CommunicationImportAttachmentSchema = z
  .object({
    url: httpUrl().describe("URL"),
    ordinal: z.number().int().nonnegative().optional().describe("Ordinal"),
    caption: z.string().optional().describe("Caption"),
    metadata: z.record(z.unknown()).optional().describe("Metadata"),
  })
  .passthrough();
export type CommunicationImportAttachment = z.input<
  typeof CommunicationImportAttachmentSchema
>;

// Import row schema — adds external_id resolution fields so demo data and bulk
// imports can resolve foreign keys by source identifier (mirrors other
// ImportRowSchema patterns: opportunity_external_id, customer_external_id, …).
export const CommunicationImportRowSchema = CommunicationInsertSchema.extend({
  // ADV-1269: override the base z.array(z.unknown()) so each attachment's url is
  // validated as absolute http(s) at the import row level (see
  // CommunicationImportAttachmentSchema above).
  attachments: z
    .array(CommunicationImportAttachmentSchema)
    .optional()
    .describe("Attachments"),
  // ADV-1068: external_id is a required, non-blank idempotency key on every
  // import-row schema — see RequiredExternalIdSchema in import.schema.ts. This
  // file has no shared constant, so the constraint is written inline. `.trim()`
  // before `.min(1)` also rejects a whitespace-only value.
  external_id: z
    .string()
    .trim()
    .min(1, "external_id is required and must not be blank")
    .describe("External ID"),
  // ADV-1398: optional alias column (see AdditionalExternalIdsSchema in
  // import.schema.ts). Defined inline here because this schema lives outside
  // import.schema.ts; without it Zod would strip the column before the import
  // pipeline's parseAdditionalExternalIds runs, silently dropping aliases for
  // communication imports.
  additional_external_ids: z
    .string()
    .trim()
    .optional()
    .describe("Additional External IDs"),
  entity_external_id: optionalExternalId().describe("Entity External ID"),
  opportunity_external_id: optionalExternalId().describe(
    "Opportunity External ID",
  ),
  contact_external_id: optionalExternalId().describe("Contact External ID"),
  actor_external_id: optionalExternalId().describe("Author External ID"),
});
export type CommunicationImportRow = z.input<
  typeof CommunicationImportRowSchema
>;

// Convenience input for the addNote() service helper.
export const AddNoteInputSchema = z.object({
  entity_type: z.enum(communicationEntityTypes).describe("Entity Type"),
  entity_id: z.string().uuid().describe("Entity"),
  body: z.string().min(1).describe("Body"),
  actor_role_id: optionalUuid().describe("Author"),
  // ADV-1200: note-channel imports forward the row-level idempotency key so the
  // note path carries the same external_id guard as the non-note path.
  external_id: z.string().optional().describe("External ID"),
  source: optionalEnum(communicationSources).describe("Source"),
});
export type AddNoteInput = z.infer<typeof AddNoteInputSchema>;
